/**
 * Small pieces shared by the builder-side Get-it-built screens (Find work,
 * Send an estimate, Builder profile, Your listings & work). Labels only — every
 * number on these screens comes from the API.
 * Doc: docs/marketplace_get_it_built.md
 */
import { Link } from 'react-router-dom'
import { money } from '@/api/marketplace'
import { KIND_LABELS, type BuildRequest, type QuoteStatus, type RequestKind } from '@/api/marketplaceBuild'

export const QUOTE_STATUS: Record<QuoteStatus, [string, string]> = {
  draft: ['Draft — not sent', ''],
  sent: ['Sent', 'info'],
  revision_asked: ['Client asked for changes', 'warn'],
  superseded: ['Replaced by a newer version', ''],
  declined: ['Declined by the client', 'bad'],
  withdrawn: ['Withdrawn', ''],
  accepted: ['Hired', 'ok'],
  not_chosen: ['Another builder was chosen', ''],
}

export function QuotePill({ status }: { status: QuoteStatus }) {
  const [t, cls] = QUOTE_STATUS[status] || [status, '']
  return <span className={`mkt-pill ${cls}`}>{t}</span>
}

const KIND_TONE: Record<RequestKind, string> = { new: 'info', change: '', site: 'info', analytics: 'ok' }

export function KindPill({ kind, label }: { kind: RequestKind; label?: string }) {
  return <span className={`mkt-pill ${KIND_TONE[kind] ?? ''}`}>{label || KIND_LABELS[kind] || kind}</span>
}

export function budgetText(r: Pick<BuildRequest, 'budget' | 'currency'>): string {
  const b = r.budget
  if (!b || b.mode !== 'range') return 'Open to estimates'
  if (b.min_minor && b.max_minor) return `${money(b.min_minor, r.currency)} – ${money(b.max_minor, r.currency)}`
  if (b.max_minor) return `Up to ${money(b.max_minor, r.currency)}`
  if (b.min_minor) return `From ${money(b.min_minor, r.currency)}`
  return 'Open to estimates'
}

/** "2 days ago" / "in 5 days" — plain words for a date. */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const ms = new Date(iso).getTime() - Date.now()
  if (Number.isNaN(ms)) return ''
  const abs = Math.abs(ms)
  const mins = Math.round(abs / 60000)
  const hours = Math.round(abs / 3600000)
  const days = Math.round(abs / 86400000)
  const txt = mins < 60 ? `${Math.max(1, mins)} minute${mins === 1 ? '' : 's'}` : hours < 24 ? `${hours} hour${hours === 1 ? '' : 's'}` : `${days} day${days === 1 ? '' : 's'}`
  return ms < 0 ? `${txt} ago` : `in ${txt}`
}

export function Avatar({ text, size = 48, available }: { text: string; size?: number; available?: boolean }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flex: '0 0 auto' }}>
      <span aria-hidden="true" className="mkt-tile" style={{ width: size, height: size, borderRadius: 999, background: 'var(--color-accent)', fontSize: Math.round(size * 0.34) }}>{text || '?'}</span>
      {available !== undefined && (
        <span title={available ? 'Available for new work' : 'Not taking new work'} style={{
          position: 'absolute', bottom: Math.round(size * 0.04), right: Math.round(size * 0.04), width: Math.max(12, Math.round(size * 0.19)), height: Math.max(12, Math.round(size * 0.19)),
          borderRadius: 999, border: '3px solid var(--color-bg-secondary)', background: available ? 'var(--color-success)' : 'var(--color-text-faint)',
        }} />
      )}
    </span>
  )
}

/** The reason this person can't send estimates (from the API), with a way forward when it is about the plan. */
export function EligibleBanner({ eligible }: { eligible: string | null | undefined }) {
  if (!eligible) return null
  const aboutPlan = /\bpro\b|plan/i.test(eligible)
  return (
    <div role="status" className="mkt-banner warn">
      <div><strong>You can't send estimates yet</strong>{eligible}</div>
      {aboutPlan && <Link to="/payments/plans" className="btn btn-primary">See plans</Link>}
    </div>
  )
}

export function Loading({ text = 'Loading…' }: { text?: string }) {
  return <div className="mkt-wrap" style={{ padding: '48px 20px' }} role="status"><span className="mkt-muted">{text}</span></div>
}
